import { TemporalDayOfWeek } from "@redotech/util/temporal";
import { assertNever } from "@redotech/util/type";
import { isUnitedStatesHoliday } from "../../date/business-day";
import {
  Carriers,
  isFedexService,
  isUpsService,
  isValidFedexDeliveryDate,
  isValidUpsDeliveryDate,
} from "../../fulfillments/fulfillment-carriers-and-services";
import { OutboundRate } from "../../outbound-labels/outbound-labels";

export function isValidDeliveryDate({
  date,
  carrier,
  service,
}: {
  date: Temporal.ZonedDateTime;
  carrier: Carriers;
  service: OutboundRate["service"];
}): boolean {
  if (isUnitedStatesHoliday({ date, businessEntity: carrier })) {
    return false;
  }

  switch (carrier) {
    case Carriers.FEDEX: {
      if (isFedexService(service)) {
        return isValidFedexDeliveryDate({ date, service });
      } else {
        return (
          date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
          date.dayOfWeek !== TemporalDayOfWeek.SUNDAY
        );
      }
    }
    case Carriers.UPS: {
      if (isUpsService(service)) {
        return isValidUpsDeliveryDate({ date, service });
      } else {
        return (
          date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
          date.dayOfWeek !== TemporalDayOfWeek.SUNDAY
        );
      }
    }
    case Carriers.FEDEX_SMART_POST:
    case Carriers.UPS_SUREPOST:
    case Carriers.DHL_ECS:
    case Carriers.APC:
    case Carriers.VEHO:
    case Carriers.ASENDIA:
    case Carriers.DHL_EXPRESS: {
      return (
        date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
        date.dayOfWeek !== TemporalDayOfWeek.SUNDAY
      );
    }
    case Carriers.USPS:
    case Carriers.USPS_RETURNS:
    case Carriers.USPS_SHIP:
    case Carriers.GLS: {
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    }
    case Carriers.AMAZON: {
      return true;
    }
    case Carriers.FIRST_MILE: {
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    }
    case Carriers.AUSTRALIA_POST: {
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    }
    case Carriers.CANADA_POST: {
      return (
        date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
        date.dayOfWeek !== TemporalDayOfWeek.SUNDAY
      );
    }
    case Carriers.ROYAL_MAIL_V3: {
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    }
    case Carriers.SPEE_DEE: {
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    }
    case Carriers.PASSPORT_GLOBAL: {
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    }
    case Carriers.LANDMARK_GLOBAL: {
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    }
    case Carriers.ETA_EXPRESS: {
      return true;
    }
    case Carriers.EPOST_GLOBAL: {
      return date.dayOfWeek !== TemporalDayOfWeek.SUNDAY;
    }
    case Carriers.ONTRAC: {
      return true;
    }
    case Carriers.GLOBALE: {
      return true;
    }
    case Carriers.UPS_MAIL_INNOVATIONS: {
      return (
        date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
        date.dayOfWeek !== TemporalDayOfWeek.SUNDAY
      );
    }
    case Carriers.PUROLATOR: {
      return (
        date.dayOfWeek !== TemporalDayOfWeek.SATURDAY &&
        date.dayOfWeek !== TemporalDayOfWeek.SUNDAY
      );
    }
    case Carriers.UNIUNI: {
      return true;
    }
    case Carriers.STALLION_EXPRESS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP: {
      return true;
    }
    default:
      assertNever(carrier);
  }
}

export function maybeIncrementToNextValidDeliveryDate({
  date,
  carrier,
  service,
}: {
  date: Temporal.ZonedDateTime;
  carrier: Carriers;
  service: OutboundRate["service"];
}): Temporal.ZonedDateTime {
  let uncheckedDate = date;
  while (!isValidDeliveryDate({ date: uncheckedDate, carrier, service })) {
    uncheckedDate = uncheckedDate.add({ days: 1 });
  }
  return uncheckedDate;
}
